import React, { useState, useEffect } from "react";
import {
  Github,
  Twitter,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from "lucide-react";

const App = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [copied, setCopied] = useState(false);
  const [currentSupply, setCurrentSupply] = useState("795,491,916");

  const solanaAddress = "YMhzCzmvWhnU15BGq8FYj41XvQzphvtqEUQosCppump";

  //

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ["home", "about", "roadmap", "tokenomics", "faqs"];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: any) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  const toggleFaq = (index: any) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(solanaAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // FAQ Data
  const faqData = [
    {
      question: "What is Night Riders ($RIDERS)?",
      answer:
        "Night Riders ($RIDERS) is a revolutionary crypto project combining digital art, community engagement, and blockchain technology. Built on the Solana network, it offers unique opportunities for collectors, traders, and crypto enthusiasts.",
    },
    {
      question: "What makes $RIDERS unique?",
      answer:
        "Our project stands out through its innovative blend of art-driven tokenomics, community governance, and sustainable growth model. We've created a unique ecosystem where creativity meets blockchain technology.",
    },
    {
      question: "Is $RIDERS safe to invest in?",
      answer:
        "We prioritize security with audited smart contracts, transparent tokenomics, and a clear development roadmap. Always do your own research and invest responsibly within your means.",
    },
    {
      question: "How can I get involved with $RIDERS?",
      answer:
        "Join our community by purchasing $RIDERS tokens, participating in our Discord discussions, following our social media, and engaging with our various community initiatives.",
    },
    {
      question: "What is the roadmap for $RIDERS?",
      answer:
        "Our roadmap includes NFT launches, platform development, community expansion, and strategic partnerships. Check our roadmap section for detailed phase-by-phase plans.",
    },
    {
      question: "How does $RIDERS support charity and community initiatives?",
      answer:
        "We allocate a portion of all transactions to our community wallet, funding various charitable causes and community projects selected through community voting.",
    },
    {
      question: "What utilities or benefits does $RIDERS offer to holders?",
      answer:
        "Holders enjoy exclusive NFT drops, governance rights, early access to new features, staking rewards, and special community events.",
    },
    {
      question: "Where can I buy $RIDERS tokens?",
      answer:
        "You can purchase $RIDERS tokens through various DEXs on the Solana network. Always verify the contract address and trade safely.",
    },
  ];

  return (
    <div className="h-screen lg:overflow-y-scroll md:overflow-y-scroll snap-y snap-mandatory bg-[#040B2C] text-white">
      {/* Hero Background Gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#040B2C] via-[#0A1A4D] to-[#040B2C] -z-10" />

      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#040B2C]/95 backdrop-blur-lg border-b border-blue-900/50"
            : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex space-x-4 sm:space-x-6">
            {["Home", "About", "Roadmap", "Tokenomics"].map(
              (
                item // "FAQ's"
              ) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-sm sm:text-lg tracking-wide sm:tracking-wider transition-all duration-300 ${
                    activeSection === item.toLowerCase()
                      ? "text-blue-400 font-medium transform scale-105"
                      : "text-gray-300 hover:text-white hover:transform hover:scale-105"
                  }`}
                >
                  {item}
                </button>
              )
            )}
          </div>
          <div className="flex space-x-3 sm:space-x-4">
            <a
              href="https://x.com/nightriders1014"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="x.svg" className="w-[25px]" alt="Icon 1" />
            </a>
            <a
              href="https://www.tiktok.com/@night.riders06?_t=8sZge5TEwQs&_r=1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="tok.svg" className="w-[25px]" alt="Icon 2" />
            </a>
            <a
              href="https://t.me/TheNightRidersM"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="tele.svg" className="w-[25px]" alt="Icon 3" />
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="h-screen snap-start flex flex-col items-center justify-center relative"
      >
        {/* Hero Background */}
        <div className="absolute inset-0">
          <img
            src="placeholder.jpg"
            alt="Night sky background"
            className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105 object-[75%] sm:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#040B2C]/50 to-[#040B2C]" />
        </div>

        {/* CTA Section */}
        <div className="relative mt-auto mb-16 sm:mb-32 flex flex-col items-center">
          <button
            className="px-6 sm:px-12 py-3 sm:py-4 text-sm sm:text-xl bg-blue-500 hover:bg-blue-600 rounded-full 
                    transition-all duration-300 mb-6 sm:mb-8 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50"
            onClick={() =>
              window.open(
                "https://jup.ag/swap/SOL-YMhzCzmvWhnU15BGq8FYj41XvQzphvtqEUQosCppump",
                "_blank"
              )
            }
          >
            Buy $RIDERS
          </button>

          <div
            className="bg-blue-900/20 backdrop-blur-sm rounded px-4 py-3 sm:px-6 sm:py-4 max-w-sm sm:max-w-3xl mx-4 
          border border-blue-500/20 transition-all duration-300 hover:border-blue-400/40"
          >
            <div className="flex items-center justify-between">
              <p className="text-blue-200 font-mono text-sm sm:text-lg select-all">
                SOLANA CA: {solanaAddress}
              </p>
              <button
                onClick={copyToClipboard}
                className="sm:ml-4 p-2 hover:bg-blue-800/30 rounded transition-colors"
              >
                {copied ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-4 h-8 sm:w-6 sm:h-10 border-2 border-blue-400 rounded-full flex items-start justify-center p-1 sm:p-2">
            <div className="w-1 h-2 sm:w-1 sm:h-3 bg-blue-400 rounded-full animate-scroll" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="min-h-screen snap-start flex flex-col items-center justify-center relative px-4 py-20 md:py-0"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[#040B2C] opacity-90" />
          <div className="absolute inset-0">
            <img
              src="/api/placeholder/1920/1080"
              alt="Background texture"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#040B2C]/80 via-[#0A1A4D]/50 to-[#040B2C] animate-pulse" />

          {/* Animated gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" />
        </div>

        <div className="w-full max-w-6xl mx-auto relative z-10">
          <div className="relative mb-16">
            <h2
              className="text-5xl md:text-7xl font-bold text-center bg-gradient-to-r from-blue-400 via-white to-blue-400 
          bg-clip-text text-transparent transform transition-all duration-700 hover:scale-105"
            >
              About
            </h2>
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-blue-500/20 blur-xl opacity-50" />
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <div
                className="relative backdrop-blur-sm rounded-3xl p-8 border border-blue-500/10 
            bg-gradient-to-br from-blue-900/10 to-purple-900/10 shadow-[0_0_50px_rgba(0,0,255,0.1)]"
              >
                <div className="text-blue-100 text-lg md:text-2xl leading-relaxed space-y-8">
                  <p className="transform transition-all duration-500 hover:translate-x-2">
                    Welcome to Night Riders ($RIDERS): A Movement Fueled by
                    Purpose, Community, and Impact. At Night Riders, we believe
                    in creating a brighter future through the power of
                    community, innovation, and meaningful action.
                  </p>
                  <p className="transform transition-all duration-500 hover:translate-x-2">
                    We're a global movement dedicated to combining the financial
                    opportunities of blockchain technology with the vision of
                    giving back and fostering transparency.
                  </p>
                  <p className="transform transition-all duration-500 hover:translate-x-2">
                    Our mission is simple yet powerful: to empower our community
                    while making a tangible impact in the real world. We aim to
                    redefine the relationship between crypto and society by
                    introducing sustainable initiatives, supporting important
                    causes, and maintaining an open dialogue with our growing
                    family of $RIDERS holders.
                  </p>
                </div>
              </div>
            </div>

            <div className="hidden md:block w-96">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-xl opacity-50" />
                <div className="relative">
                  <img
                    src="riderimage.png"
                    alt="Night Riders"
                    className="w-full h-[600px] object-cover rounded-t-full rounded-b-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Partner Buttons */}
          <div className="mt-16 relative">
            <div
              className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 
          rounded-3xl blur-xl opacity-50 animate-pulse"
            />

            <div className="relative grid grid-cols-2 md:grid-cols-6 gap-4 items-center">
              {[
                {
                  name: "CoinGecko",
                  logo: "gecko.png",
                  link: "https://www.coingecko.com/en/coins/night-riders",
                },
                {
                  name: "CoinMarketCap",
                  logo: "cmc.png",
                  link: "https://coinmarketcap.com/dexscan/solana/HjMZWSNRrwuiUHRdQWiqAejATp6i71UAvgpdJPqyfoRV/",
                },
                {
                  name: "DexScreener",
                  logo: "dex.png",
                  link: "https://dexscreener.com/solana/54gapwatbmhgdtygwzff8ost1fxefm4ztnkgmvnqynlt",
                },
              ].map((partner, index) => (
                <a
                  key={index}
                  href={partner.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="w-full h-8 object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section
        id="roadmap"
        className="min-h-screen snap-start flex flex-col items-center justify-center relative px-4 py-20 md:py-0"
      >
        <div className="relative z-10 w-full max-w-7xl">
          <h2
            className="text-4xl md:text-7xl font-bold mb-10 md:mb-20 text-center bg-gradient-to-r from-blue-400 via-white to-blue-400 
        bg-clip-text text-transparent transform transition-all duration-700 hover:scale-105"
          >
            Roadmap
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                phase: "1",
                gradient: "from-indigo-600 via-blue-500 to-cyan-400",
                items: [
                  "Platform Security and Analytics",
                  "Developed key $RIDERS smart",
                  "Airdrop",
                  "Listed $RIDERS on DEX",
                  "Community growth",
                  "Portfolio Setup",
                  "Marketing campaign",
                ],
              },
              {
                phase: "2",
                gradient: "from-emerald-600 via-teal-500 to-cyan-400",
                items: [
                  "NFT airdrops ongoing community",
                  "Product and pitch live Whitepaper",
                  'Launch site "Ride for a Space"',
                  "Utilities",
                  "Partnerships with crypto leaders",
                ],
              },
              {
                phase: "3",
                gradient: "from-amber-500 via-yellow-500 to-orange-400",
                items: [
                  "Strategic Partnerships and listing",
                  "CEX listing",
                  "Marketing campaign pt.2",
                  "Community growth phase 2",
                  "Sustainable token burn & rewards",
                ],
              },
              {
                phase: "4",
                gradient: "from-pink-600 via-fuchsia-500 to-pink-400",
                items: [
                  "Beyond Roadmap Listings",
                  "Global community expansion",
                  "Transparency",
                  "Regular incentives & surprises",
                  "Beneficial leader board",
                ],
              },
            ].map(({ phase, gradient, items }, index) => (
              <div
                key={phase}
                className="group relative transform transition-all duration-500 hover:scale-105"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-br ${gradient} rounded-3xl blur 
              opacity-30 group-hover:opacity-50 transition-opacity duration-500`}
                />
                <div className="relative h-full backdrop-blur-xl bg-black/50 rounded-3xl p-6 md:p-8 border border-white/10">
                  <div className="text-6xl md:text-8xl font-bold mb-6 md:mb-8 bg-gradient-to-br from-white to-white/70 bg-clip-text text-transparent">
                    {phase}
                  </div>
                  <ul className="space-y-3 md:space-y-4">
                    {items.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start space-x-3 transform transition-all duration-300 hover:translate-x-2"
                      >
                        <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
                        <span className="text-sm md:text-base text-gray-300">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section
        id="tokenomics"
        className="min-h-screen snap-start flex flex-col items-center justify-center relative px-4 py-20 md:py-0"
      >
        <div className="w-full max-w-6xl relative z-10">
          <h2
            className="text-4xl md:text-7xl font-bold mb-10 md:mb-20 text-center bg-gradient-to-r from-blue-400 via-white 
        to-blue-400 bg-clip-text text-transparent transform transition-all duration-700 hover:scale-105"
          >
            Tokenomics
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Image section */}
            <div className="hidden md:block w-96">
              <div className="relative aspect-square">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-xl opacity-50" />
                <div className="relative w-full h-full p-4">
                  <img
                    src="burn.jpg"
                    alt="Burn Effect"
                    className="mt-10 w-full h-full object-cover rounded-3xl shadow-[0_0_30px_rgba(0,0,255,0.2)]"
                  />
                </div>
              </div>
            </div>

            {/* Content section */}
            <div className="flex-1">
              <div className="text-center mb-10 md:mb-16 transform transition-all duration-500 hover:scale-105">
                <h3
                  className="text-2xl md:text-3xl mb-4 md:mb-6 bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent 
              font-medium tracking-wider"
                >
                  SUPPLY BREAKDOWN
                </h3>
                <div className="relative inline-block">
                  <div
                    className="absolute -inset-4 bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-cyan-500 
                rounded-2xl blur-xl opacity-30"
                  />
                  <p
                    className="relative text-5xl md:text-7xl font-bold bg-gradient-to-r from-white via-cyan-400 to-white 
                bg-clip-text text-transparent"
                  >
                    {currentSupply}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {[
                  {
                    text: "CONTRACT RENOUNCED",
                    gradient: "from-cyan-500 via-blue-500 to-cyan-500",
                  },
                  {
                    text: "COMMUNITY DRIVEN",
                    gradient: "from-fuchsia-500 via-purple-500 to-fuchsia-500",
                  },
                  {
                    text: "PROJECT WITH PRODUCT",
                    gradient: "from-pink-500 via-rose-500 to-pink-500",
                  },
                ].map(({ text, gradient }, index) => (
                  <div
                    key={text}
                    className="group relative transform transition-all duration-500 hover:scale-105"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div
                      className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-2xl blur 
                  opacity-30 group-hover:opacity-50 transition-opacity duration-500`}
                    />
                    <div className="relative backdrop-blur-xl bg-black/50 rounded-2xl p-6 md:p-8 border border-white/10">
                      <p
                        className="text-lg md:text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r 
                    from-cyan-400 to-white tracking-wider text-center"
                      >
                        {text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faqs"
        className="min-h-screen snap-start flex flex-col items-center justify-center relative px-4 py-20 md:py-0"
      >
        <div className="w-full max-w-6xl relative z-10">
          <h2
            className="text-4xl md:text-7xl font-bold mb-10 md:mb-16 text-center bg-gradient-to-r from-blue-400 via-white to-blue-400 
        bg-clip-text text-transparent transform transition-all duration-700 hover:scale-105"
          >
            FAQ's
          </h2>

          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1 w-full space-y-4 relative">
              {[
                {
                  question: "What is Night Riders ($RIDERS)?",
                  answer:
                    "Night Riders ($RIDERS) is a revolutionary crypto project combining digital art, community engagement, and blockchain technology. Built on the Solana network, it offers unique opportunities for collectors, traders, and crypto enthusiasts.",
                },
                {
                  question: "What makes $RIDERS unique?",
                  answer:
                    "Our project stands out through its innovative blend of art-driven tokenomics, community governance, and sustainable growth model. We've created a unique ecosystem where creativity meets blockchain technology.",
                },
                {
                  question: "Is $RIDERS safe to invest in?",
                  answer:
                    "We prioritize security with audited smart contracts, transparent tokenomics, and a clear development roadmap. Always do your own research and invest responsibly within your means.",
                },
                {
                  question: "How can I get involved with $RIDERS?",
                  answer:
                    "Join our community by purchasing $RIDERS tokens, participating in our Discord discussions, following our social media, and engaging with our various community initiatives.",
                },
                {
                  question: "What is the roadmap for $RIDERS?",
                  answer:
                    "Our roadmap includes NFT launches, platform development, community expansion, and strategic partnerships. Check our roadmap section for detailed phase-by-phase plans.",
                },
                {
                  question:
                    "How does $RIDERS support charity and community initiatives?",
                  answer:
                    "We allocate a portion of all transactions to our community wallet, funding various charitable causes and community projects selected through community voting.",
                },
                {
                  question:
                    "What utilities or benefits does $RIDERS offer to holders?",
                  answer:
                    "Holders enjoy exclusive NFT drops, governance rights, early access to new features, staking rewards, and special community events.",
                },
                {
                  question: "Where can I buy $RIDERS tokens?",
                  answer:
                    "You can purchase $RIDERS tokens through various DEXs on the Solana network. Always verify the contract address and trade safely.",
                },
              ].map((faq, index) => (
                <div
                  key={index}
                  className="group relative transform transition-all duration-500 hover:translate-x-2"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div
                    className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/50 to-blue-300/50 rounded-xl 
                blur opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                  />
                  <div className="relative bg-blue-900/20 backdrop-blur-sm rounded-xl border border-blue-500/20 overflow-hidden">
                    <button
                      className="w-full px-4 md:px-6 py-4 flex justify-between items-center text-left"
                      onClick={() => toggleFaq(index)}
                    >
                      <h3 className="text-base md:text-lg text-blue-200 pr-4">
                        {faq.question}
                      </h3>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-blue-400 transition-transform duration-300 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-blue-400 transition-transform duration-300 flex-shrink-0" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <div className="px-4 md:px-6 py-4 text-blue-100 border-t border-blue-500/20 transform transition-all duration-500">
                        <p className="text-sm md:text-base leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block w-96 self-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-50" />
                <div className="relative">
                  <img
                    src="roadmap.jpg"
                    alt="Roadmap"
                    className="w-full aspect-square object-cover rounded-3xl shadow-[0_0_30px_rgba(0,0,255,0.2)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default App;
